import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";

export default function Dashboard() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchDrivers();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);

    const subscription = supabase
      .channel("drivers")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "drivers" },
        () => {
          fetchDrivers();
        }
      )
      .subscribe();

    return () => {
      window.removeEventListener("resize", handleResize);
      supabase.removeChannel(subscription);
    };
  }, []);

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("*")
      .order("submitted_at", { ascending: false });
    setDrivers(data || []);
    setLoading(false);
  };

  const stats = {
    total: drivers.length,
    pending: drivers.filter((d) => d.drug_test === "pending").length,
    passed: drivers.filter(
      (d) => d.drug_test === "pass" && d.cdl_history === "pass"
    ).length,
    failed: drivers.filter(
      (d) => d.drug_test === "fail" || d.cdl_history === "fail"
    ).length,
  };

  const getBadge = (status) => {
    const styles = {
      pass: "bg-green-100 text-green-800",
      fail: "bg-red-100 text-red-800",
      pending: "bg-amber-100 text-amber-800",
      sent: "bg-blue-100 text-blue-800",
    };
    return `t-badge px-2 py-0.5 rounded-full ${
      styles[status] || styles.pending
    }`;
  };

  const getInitials = (first, last) => `${first?.[0] || ""}${last?.[0] || ""}`;

  const statCards = [
    { label: "Total applicants", value: stats.total, valueColor: "#111" },
    { label: "Drug test pending", value: stats.pending, valueColor: "#92400e" },
    { label: "Passed all checks", value: stats.passed, valueColor: "#166534" },
    { label: "Failed", value: stats.failed, valueColor: "#991b1b" },
  ];

  const filteredDrivers = drivers.filter(
    (driver) =>
      driver.first_name?.toLowerCase().includes(search.toLowerCase()) ||
      driver.last_name?.toLowerCase().includes(search.toLowerCase()) ||
      driver.phone?.includes(search) ||
      driver.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      {!isMobile && <Sidebar />}

      <div className="flex-1" style={{ minWidth: 0 }}>
        {isMobile && <MobileHeader title="Driver pipeline" />}

        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h1 className="t-page-title">Driver pipeline</h1>
          <div className="flex items-center gap-2">
            <input
              placeholder="Search drivers..."
              style={{
                width: isMobile ? "130px" : "192px",
                fontSize: "13px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "6px 12px",
                outline: "none",
                color: "#111",
              }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              style={{
                background: "#3a9e5f",
                color: "#fff",
                fontSize: "12px",
                fontWeight: "500",
                padding: "6px 12px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
                letterSpacing: "-0.01em",
              }}
            >
              + Add
            </button>
          </div>
        </div>

        <div style={{ padding: isMobile ? "12px" : "24px" }}>
          {/* Stat cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            {statCards.map((stat) => (
              <div
                key={stat.label}
                className="bg-white border border-gray-200 rounded-xl"
                style={{ padding: "16px" }}
              >
                <div className="t-label" style={{ marginBottom: "6px" }}>
                  {stat.label}
                </div>
                <div className="t-stat" style={{ color: stat.valueColor }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          {/* Section label */}
          <div
            className="t-label"
            style={{ marginBottom: "10px", color: "#6b7280" }}
          >
            Recent drivers
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Table header — desktop only */}
            {!isMobile && (
              <div className="grid grid-cols-6 px-4 py-2 border-b border-gray-100 bg-gray-50">
                {[
                  "Driver",
                  "Contact",
                  "Drug test",
                  "CDL history",
                  "Contract",
                  "",
                ].map((h) => (
                  <div key={h} className="t-table-header">
                    {h}
                  </div>
                ))}
              </div>
            )}

            {loading ? (
              <div
                className="px-4 py-8 text-center t-body"
                style={{ color: "#9ca3af" }}
              >
                Loading...
              </div>
            ) : filteredDrivers.length === 0 ? (
              <div
                className="px-4 py-8 text-center t-body"
                style={{ color: "#9ca3af" }}
              >
                No drivers yet
              </div>
            ) : (
              filteredDrivers.map((driver) =>
                isMobile ? (
                  // Mobile card
                  <div
                    key={driver.id}
                    onClick={() => navigate(`/drivers/${driver.id}`)}
                    className="px-4 py-3 border-b border-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{
                            background: "#eaf3de",
                            color: "#3a9e5f",
                            fontSize: "11px",
                            fontWeight: "500",
                          }}
                        >
                          {getInitials(driver.first_name, driver.last_name)}
                        </div>
                        <div>
                          <div className="t-name">
                            {driver.first_name} {driver.last_name}
                          </div>
                          <div className="t-data" style={{ color: "#6b7280" }}>
                            {driver.phone}
                          </div>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#3a9e5f",
                          fontWeight: "500",
                        }}
                      >
                        View →
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <span className={getBadge(driver.drug_test)}>
                        {driver.drug_test}
                      </span>
                      <span className={getBadge(driver.cdl_history)}>
                        {driver.cdl_history}
                      </span>
                      <span className={getBadge(driver.contract_sent)}>
                        {driver.contract_sent}
                      </span>
                    </div>
                  </div>
                ) : (
                  // Desktop row
                  <div
                    key={driver.id}
                    className="grid grid-cols-6 px-4 py-3 border-b border-gray-50 items-center hover:bg-gray-50 transition-colors"
                  >
                    {/* Driver name + date */}
                    <div className="flex items-center gap-2">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: "#eaf3de",
                          color: "#3a9e5f",
                          fontSize: "11px",
                          fontWeight: "500",
                        }}
                      >
                        {getInitials(driver.first_name, driver.last_name)}
                      </div>
                      <div>
                        <div className="t-name">
                          {driver.first_name} {driver.last_name}
                        </div>
                        <div className="t-data" style={{ color: "#9ca3af" }}>
                          {new Date(driver.submitted_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {/* Contact */}
                    <div>
                      <div className="t-data" style={{ color: "#374151" }}>
                        {driver.phone}
                      </div>
                      <div className="t-data" style={{ color: "#9ca3af" }}>
                        {driver.email}
                      </div>
                    </div>

                    {/* Badges */}
                    <div>
                      <span className={getBadge(driver.drug_test)}>
                        {driver.drug_test}
                      </span>
                    </div>
                    <div>
                      <span className={getBadge(driver.cdl_history)}>
                        {driver.cdl_history}
                      </span>
                    </div>
                    <div>
                      <span className={getBadge(driver.contract_sent)}>
                        {driver.contract_sent}
                      </span>
                    </div>

                    {/* View link */}
                    <div>
                      <button
                        onClick={() => navigate(`/drivers/${driver.id}`)}
                        style={{
                          fontSize: "12px",
                          color: "#3a9e5f",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: "500",
                        }}
                      >
                        View →
                      </button>
                    </div>
                  </div>
                )
              )
            )}
          </div>

          {/* Driver count */}
          <div
            className="t-label"
            style={{ marginTop: "10px", color: "#9ca3af" }}
          >
            {filteredDrivers.length} driver
            {filteredDrivers.length !== 1 ? "s" : ""}
            {search ? ` matching "${search}"` : " total"}
          </div>
        </div>
      </div>
    </div>
  );
}
